import { create } from 'zustand';
import { buildFullThemeColors, DEFAULT_BRAND_TRIPLET } from './colorUtils';

export interface ThemeColors {
  // Brand & Accent
  primary: string;
  primaryLight: string;
  primaryDark: string;
  primaryPressed: string;
  primaryFade: string;
  primaryHalf: string;
  primaryUltra: string;
  primaryText: string;
  onPrimary: string; // Text color specifically for use on primary background

  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  secondaryPressed: string;
  secondaryFade: string;
  secondaryHalf: string;
  secondaryUltra: string;
  secondaryText: string;
  onSecondary: string;

  tertiary: string;
  tertiaryLight: string;
  tertiaryDark: string;
  tertiaryPressed: string;
  tertiaryFade: string;
  tertiaryHalf: string;
  tertiaryUltra: string;
  tertiaryText: string;
  onTertiary: string;

  // Surfaces & Layout — elevation ladder (darkest → lightest):
  // background < backgroundElevated < surfaceElevated < surface
  background: string;           // Screen canvas (darkest)
  backgroundElevated: string;   // Containers / section panels
  backgroundFade: string;
  backdrop: string;             // Modal/Overlay dimming
  surface: string;              // Cards / content tiles (lightest)
  surfaceElevated: string;      // Mid-elevation between container and card
  surfaceLight: string;         // Inset wells inside cards
  surfaceFade: string;          // Subtle backgrounds/disabled inputs
  borderSubtle: string;         // Hairline separators on chrome/cards
  divider: string;              // Thin horizontal lines
  skeleton: string;             // Shimmer loading base

  // Typography
  text: string;          // Main headings/body
  textSecondary: string; // Subtitles
  textLight: string;     // Captions/Hint text
  textInverse: string;   // White text on dark mode or vice versa

  // Borders
  border: string;
  borderLight: string;

  // Semantic States
  error: string;
  errorLight: string;
  errorFade: string;
  errorText: string;
  onError: string;
  errorUltra: string;

  success: string;
  successLight: string;
  successFade: string;
  successText: string;
  onSuccess: string;
  successUltra: string;

  warning: string;
  warningLight: string;
  warningFade: string;
  warningText: string;
  onWarning: string;
  warningUltra: string;

  alert: string;
  alertLight: string;
  alertFade: string;
  alertText: string;
  onInfo: string;
  alertUltra: string;

  danger: string; // Alias for error or specific destructive action

  // Neutral Scales
  disabled: string;
  disabledLight: string;
  disabledDark: string;
  white: string;
  black: string;
  grey: string;
  greyLight: string;
  greyDark: string;

  // Data Visualization / Nutrition Specific
  calorie: string;
  protein: string;
  carbs: string;
  fats: string;

  calorieUltra: string;
  proteinUltra: string;
  carbsUltra: string;
  fatsUltra: string;
  shadowColor: string;
}
interface ThemeState {
  /** User-chosen UI scheme (not tied to OS). */
  appearance: 'light' | 'dark';
  colors: ThemeColors;
  isInitialized: boolean;
  setAppearance: (appearance: 'light' | 'dark') => void;
  setColors: (colors: Partial<ThemeColors>) => void;
  setInitialized: (v: boolean) => void;
}

/** Initial palette: light scheme derived from DEFAULT_BRAND_TRIPLET (same pipeline as API theme). */
const defaultColors: ThemeColors = buildFullThemeColors(DEFAULT_BRAND_TRIPLET, 'light');

export const useThemeStore = create<ThemeState>(set => ({
  appearance: 'light',
  colors: defaultColors,
  isInitialized: false,
  setAppearance: appearance => set({ appearance }),
  setColors: newColors => set(s => ({ colors: { ...s.colors, ...newColors } })),
  setInitialized: v => set({ isInitialized: v }),
}));
